<template>
    <div class="selected-card-info">
        <div class="selected-card-options">
            <!-- Add Cards Button -->
            <button-primary
            v-if="dashboardWidgetAddButton && showNewCardButton"
            withExternalLabel
            buttonStyle="round"
            buttonSize="small"
            icon="plus"
            :label="t('cards.consult.addButtonSlider')"
			@clicked="showNewCardModal = true"/>
            <!-- Buttons below -->
            <button-tertiary
            v-for="button in parsedButtonsBelow"
            withExternalLabel
            buttonStyle="round"
            buttonSize="small"
            :key="`button${button.id}`"
            :label="button.text"
            :icon="button.icon"
			@clicked="parsedButtonsBelow.find(obj => obj.id === button.id).callbackFunction()"/>
            <!-- Other options -->
			<dropdown-more-options
            v-if="parsedMoreOptions.length"
			variant="tertiary"
			:label="t('cards.consult.moreOptions.moreOptions')"
			:options="parsedMoreOptions"
			@clicked="parsedMoreOptions.find(obj => obj.id === $event).callbackFunction()"/>
        </div>
		<!-- Card Info -->
		<component
		v-if="!hideCardDetail"
		:is="cardInfoDetailComponent"
		:key="selectedCard"
		:selectedCard="selectedCard"
		:dataShowing="!!virtualCardData"
		:virtualCardData="virtualCardData"
		@showVirtualCardData="emit('requestVirtualCardData')"/>
    </div>
    <!-- New Card Modal -->
    <new-card-modal
    v-if="showNewCardModal"
    :dashboardWidget="dashboardWidget || dashboardWidgetAnchor"
    @hideMe="showNewCardModal = false"/>
</template>

<script setup>
import ButtonPrimary from '@/components/generic/buttons/Primary'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import DropdownMoreOptions from '@/components/generic/dropdowns/moreOptions/MoreOptions'
import NewCardModal from '@/components/features/cards/consult/NewCardModal'

/* Helpers */
import { CARDS_TRANSFER_ROUTES, CARDS_TOPUP_ROUTES } from '@/assets/constants/cards'
import { cardsTypeForOperations, cardsProductForOperations, cardsTopupForOperations, cardsTypeEmitterForOperations } from '@/assets/scripts/mappers/cards'
import { getCurrentRouteMenuItemInfo } from '@/assets/scripts/parsers/menu'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
/* Vue */
import { ref, onMounted, computed, defineAsyncComponent } from 'vue'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['requestVirtualCardData', 'showAddM'])

/* Props
@dashboardWidgetAddButton {Boolean}
@dashboardWidget {Boolean} - prop to apply logics for this to be used on dashboardWidget
@hideCardDetail {Boolean}
@selectedCard {Object} - object with card info, needs to have:
	- id {Number, String}
	- type {String}
	- productName {String}
	- product {String}
	- status {String} - code of card status
	- statusDescriptive {String} - description of current status
	- blockable {Boolean} - if card can be blocked, it means the card is currently unblocked
	- unblockable {Boolean} - if card can be unblocked, it means the card is currently blocked
@virtualCardData {Object} - object with card info unmasked
*/
const props = defineProps({
    dashboardWidgetAddButton: Boolean,
	dashboardWidget: Boolean,
	hideCardDetail: Boolean,
	selectedCard: Object,
	virtualCardData: Object
})

const showNewCardButton = ref(false)
const showNewCardModal = ref(false)

const cardInfoDetailComponent = computed(() => {
	let template = 'StandardCardDetails'

	if (props.selectedCard.status === 'GENERATING') {
		template = 'GeneratingCardDetails'
	} else if (props.selectedCard.product === 'CVISA_VIRT') {
		template = 'VirtualCardDetails'
	}

	return defineAsyncComponent(() => import('@/components/features/cards/consult/' + template))
})

/* ***********************************************
*              More Options Callbacks            *
*********************************************** */
const redirectToTransfer = () => {
	navigateByName(CARDS_TRANSFER_ROUTES[props.selectedCard.topupMode], { selectedCardId: props.selectedCard.id })
}

const redirectToCharge = () => {
    navigateByName(CARDS_TOPUP_ROUTES[props.selectedCard.topupMode], { selectedCardId: props.selectedCard.id })
}

const redirectToWithdraw = () => {
    navigateByName(routeList.CARDS_CARDLESS_WITHDRAW_GENERATE_CODE_NAME, { selectedCardId: props.selectedCard.id })
}

const redirectToAnticipatePayment = () => {
	navigateByName(routeList.CARDS_ANTICIPATE_PAYMENT_NAME, { selectedCardId: props.selectedCard.id })
}

const redirectToChangePayment = () => {
	navigateByName(routeList.CARDS_CHANGE_PAYMENT_NAME, { selectedCardId: props.selectedCard.id })
}

const redirectToCancel = () => {
	navigateByName(routeList.CARDS_CANCEL_NAME, { selectedCardId: props.selectedCard.id })
}

const redirectToBlock = () => {
	navigateByName(routeList.CARDS_BLOCK_NAME, { selectedCardId: props.selectedCard.id })
}

const redirectToLimitsManagement = () => {
	navigateByName(routeList.CARDS_LIMITS_NAME, { selectedCardId: props.selectedCard.id })
}

/* ***********************************************
*                All Possible Options            *
*********************************************** */
const buttonsBelowCard = [{
	id: 'transfer',
	text: t('cards.consult.moreOptions.transfer'),
	icon: 'transfer',
	callbackFunction: redirectToTransfer
}, {
	id: 'charge',
	text: t('cards.consult.moreOptions.charge'),
	icon: 'card-add',
	callbackFunction: redirectToCharge
}, {
	id: 'withdraw',
	text: t('cards.consult.moreOptions.withdraw'),
	icon: 'money',
	callbackFunction: redirectToWithdraw
}]

const moreOptions = [{
	id: 'limits-management',
	text: t('cards.consult.moreOptions.limitsManagement'),
	icon: 'pig',
	callbackFunction: redirectToLimitsManagement
}, {
	id: 'pay',
	text: t('cards.consult.moreOptions.pay'),
	icon: 'payment-single',
	callbackFunction: redirectToAnticipatePayment
}, {
	id: 'change-payment',
	text: t('cards.consult.moreOptions.changePayment'),
	icon: 'credit-card',
	callbackFunction: redirectToChangePayment
}, {
	id: 'block',
	text: t('cards.consult.moreOptions.block'),
	icon: 'lock',
	callbackFunction: redirectToBlock
}, {
	id: 'unblock',
	text: t('cards.consult.moreOptions.unblock'),
	icon: 'unlock',
	callbackFunction: redirectToBlock
}, {
	id: 'cancel',
	text: t('cards.consult.moreOptions.cancel'),
	icon: 'block',
	variant: 'negative',
	callbackFunction: redirectToCancel
}]

/* ***********************************************
*             Parser Logic for Options           *
*********************************************** */
const menuEntriesForAccelarator = computed(() => {
	const routeToGetInfo = props.dashboardWidget ? routeList.CARDS_CONSULT_PATH : null

    if (!getCurrentRouteMenuItemInfo(routeToGetInfo) || !getCurrentRouteMenuItemInfo(routeToGetInfo).children.length) return []
    return getCurrentRouteMenuItemInfo(routeToGetInfo).children
})

const parsedButtonsBelow = computed(() => {
	return buttonsBelowCard.reduce((acc, button) => {
        /* Validating if card has topupMode and if menuEntry exits on menu */
        if (button.id === 'transfer' &&
            menuEntriesForAccelarator.value.find(obj => obj.label === 'cards_consult_transfer') &&
            cardsTopupForOperations.CARDS_TRANSFERS_FLEX.includes(props.selectedCard.topupMode)) {
            acc.push(button)
		}

        /* Validating if card has topupMode and if menuEntry exits on menu */
        if (button.id === 'charge' &&
            menuEntriesForAccelarator.value.find(obj => obj.label === 'cards_consult_charge') &&
            props.selectedCard.allowsTopup) {
            acc.push(button)
		}

        /* Validating if card has topupMode and if menuEntry exits on menu */
        if (button.id === 'withdraw' &&
            menuEntriesForAccelarator.value.find(obj => obj.label === 'cards_consult_cardless_withdraw_generate_code') &&
            cardsTypeEmitterForOperations.CARDS_CARDLESS_WITHDRAW_GENERATE_CODE.includes(props.selectedCard.typeEmitter) &&
            cardsProductForOperations.CARDS_CARDLESS_WITHDRAW_GENERATE_CODE.includes(props.selectedCard.product)) {
            acc.push(button)
		}

        return acc
    }, [])
})

const parsedMoreOptions = computed(() => {
	return moreOptions.reduce((acc, option) => {
		/* Validating if card has type for payment and if menuEntry exits on menu */
		if (option.id === 'pay' && menuEntriesForAccelarator.value.find(obj => obj.label === 'cards_consult_anticipate_payment')) {
			if (cardsTypeForOperations.CARDS_ANTICIPATE_PAYMENT.includes(props.selectedCard.type) &&
				cardsProductForOperations.CARDS_ANTICIPATE_PAYMENT.includes(props.selectedCard.product)) {
                acc.push(option)
            }
		}

        /* Validating if card has type for payment and if menuEntry exits on menu */
		if (option.id === 'change-payment' && menuEntriesForAccelarator.value.find(obj => obj.label === 'cards_consult_change_payment')) {
			if (cardsTypeForOperations.CARDS_CHANGE_PAYMENT.includes(props.selectedCard.type) &&
				cardsProductForOperations.CARDS_CHANGE_PAYMENT.includes(props.selectedCard.product)) {
                acc.push(option)
            }
		}

		if (option.id === 'cancel' &&
            menuEntriesForAccelarator.value.find(obj => obj.label === 'cards_consult_cancel') &&
            props.selectedCard.cancelable) {
            acc.push(option)
        }

		/* Validating if card can be blocked and if menuEntry exits on menu */
		if (option.id === 'block' && menuEntriesForAccelarator.value.find(obj => obj.label === 'cards_consult_block') && props.selectedCard.blockable) {
			acc.push(option)
		}

		/* Validating if card can be unblockable and if menuEntry exits on menu */
		if (option.id === 'unblock' && menuEntriesForAccelarator.value.find(obj => obj.label === 'cards_consult_block') && props.selectedCard.unblockable) {
			acc.push(option)
		}

		/* Validating if card can be managed and if menuEntry exits on menu */
		if (option.id === 'limits-management' &&
            menuEntriesForAccelarator.value.find(obj => obj.label === 'cards_consult_limits') &&
            cardsTypeEmitterForOperations.CARDS_LIMITS.includes(props.selectedCard.typeEmitter) &&
            cardsProductForOperations.CARDS_LIMITS.includes(props.selectedCard.product)) {
			acc.push(option)
		}

		return acc
	}, [])
})

onMounted(() => {
    const routeToGetInfo = routeList.CARDS_BASE_PATH
    const menuEntriesForAccelarator = getCurrentRouteMenuItemInfo(routeToGetInfo).children

    if (menuEntriesForAccelarator.find(obj => obj.label === 'cards_request') || menuEntriesForAccelarator.find(obj => obj.label === 'cards_activate')) {
        showNewCardButton.value = !!getCurrentRouteMenuItemInfo('/cards/request')
    }
})
</script>

<style lang="scss" scoped>
.selected-card-info {
	width: 100%;
	padding: 0 16px;

	display: flex;
	flex-direction: column;
	align-items: center;
	flex-shrink: 0;

    .selected-card-options {
        width: 100%;
        height: auto;

        display: flex;
		justify-content: center;
		flex-shrink: 0;
        gap: 16px;
    }

	:deep(.collapsible-divider-wrapper) {
		margin-top: 24px;
		padding-bottom: 0;
	}
}
</style>
